import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import { API_ENDPOINTS } from './config';

function AvatarCreation({ sessionId: propSessionId, onAvatarCreated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [localSessionId, setLocalSessionId] = useState(propSessionId || location.state?.sessionId || null);
  const [processingStep, setProcessingStep] = useState('');
  // Removed business card related state variables
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const sessionInitRef = useRef(false); // Track if session is being initialized
  const sessionCreatedRef = useRef(false); // Track if session was already created
  const cameraAutoStartedRef = useRef(false); // Track if camera was auto-started
  const avatarGeneratingRef = useRef(false); // Track if avatar generation is in progress

  // Auto-start camera on mount when redirected from conversation
  useEffect(() => {
    const autoStartCamera = async () => {
      // Only auto-start if we haven't already and there's no captured image
      if (!cameraAutoStartedRef.current && !capturedImage) {
        cameraAutoStartedRef.current = true;
        // Small delay to ensure session is created first
        setTimeout(() => {
          openCamera();
        }, 1000);
      }
    };
    
    autoStartCamera();
  }, []); // Run once on mount

  // Create session if not provided
  useEffect(() => {
    const initSession = async () => {
      // Prevent multiple session creations
      if (sessionInitRef.current || sessionCreatedRef.current || localSessionId) {
        console.log('Session already being created or was created, skipping');
        return;
      }
      
      sessionInitRef.current = true;
      
      try {
        console.log('Creating session...');
        const response = await fetch(API_ENDPOINTS.sessionsCreate, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.error === false && data.data) {
            setLocalSessionId(data.data.session_id);
            sessionCreatedRef.current = true; // Mark session as created
            console.log('Session created:', data.data.session_id);
          }
        }
      } catch (err) {
        console.error('Error creating session:', err);
      } finally {
        sessionInitRef.current = false;
      }
    };
    
    initSession();
  }, []); // Empty dependency array - only run once on mount

  // Open camera
  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      streamRef.current = stream;
      setIsCameraOpen(true);
      setError('');
      setSuccess('');
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Failed to access camera. Please grant camera permissions.');
    }
  };

  // Effect to attach stream to video element
  useEffect(() => {
    console.log('Camera effect running:', { isCameraOpen, hasStream: !!streamRef.current, hasVideo: !!videoRef.current });
    if (isCameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOpen]);

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current) {
      setError('Camera is not ready. Please try again.');
      return;
    }
    
    const video = videoRef.current;
    
    // Check if video has valid dimensions
    if (!video.videoWidth || !video.videoHeight) {
      // Try capturing with default dimensions
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext('2d');
      
      // Try to draw the current video frame
      try {
        ctx.drawImage(video, 0, 0, 640, 480);
      } catch (e) {
        setError('Failed to capture photo. Please try again.');
        return;
      }
      
      const dataUrl = canvas.toDataURL('image/png');
      setCapturedImage(dataUrl);
      stopCamera();
      return;
    }
    
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/png');
      setCapturedImage(dataUrl);
      stopCamera();
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    openCamera();
  };

  // Generate avatar directly (without business card extraction)
  const generateAvatar = async () => {
    console.log('Generate avatar clicked:', { capturedImage: !!capturedImage, sessionId: !!localSessionId });
    
    if (avatarGeneratingRef.current) {
      console.log('Avatar generation already in progress, skipping');
      return;
    }
    
    avatarGeneratingRef.current = true;
    
    if (!capturedImage) {
      avatarGeneratingRef.current = false;
      setError('Please capture a photo first');
      return;
    }
    
    if (!localSessionId) {
      avatarGeneratingRef.current = false;
      setError('Creating session... Please try again in a moment.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setSuccess('');

    try {
      const base64Data = capturedImage.split(',')[1];
      const blob = atob(base64Data);
      const arrayBuffer = new ArrayBuffer(blob.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < blob.length; i++) {
        uint8Array[i] = blob.charCodeAt(i);
      }
      const imageFile = new Blob([uint8Array], { type: 'image/png' });

      const formData = new FormData();
      formData.append('file', imageFile, 'avatar.png');

      console.log('Generating AI Avatar directly...');
      setProcessingStep('Generating your AI avatar...');
      
      const { url: avatarUrl, sessionId: storedSessionId } = API_ENDPOINTS.avatarGenerate();
      const visitorId = location.state?.visitorId || localStorage.getItem('visitor_id') || 'avatar_' + Date.now();
      console.log('Using visitorId:', visitorId);

      const avatarResponse = await fetch(avatarUrl, {
        method: 'POST',
        headers: {
          'session-id': storedSessionId || localSessionId,
        },
        body: formData,
      });

      console.log('Avatar API response status:', avatarResponse.status);

      if (avatarResponse.ok) {
        const avatarApiData = await avatarResponse.json();
        console.log('Avatar API response:', avatarApiData);
        
        if (avatarApiData.error === false && avatarApiData.data) {
          setSuccess('Avatar generated successfully!');
          
          const avatarId = avatarApiData.data.avatar_id || avatarApiData.data.id || avatarApiData.data;
          
          if (avatarId) {
            navigate(`/avatar/display/${visitorId}/${avatarId}`, { 
              state: { 
                sessionId: localSessionId,
                capturedImage: capturedImage 
              } 
            });
          } else {
            if (onAvatarCreated) {
              onAvatarCreated({ 
                ...avatarApiData.data, 
                sessionId: localSessionId 
              });
            }
          }
        } else {
          setError(avatarApiData.message || 'Failed to generate avatar');
        }
      } else {
        const errorData = await avatarResponse.json();
        console.error('Avatar API error:', errorData);
        setError(errorData.message || 'Failed to generate avatar');
      }
    } catch (err) {
      console.error('Error generating avatar:', err);
      setError('Failed to generate avatar. Please try again.');
    } finally {
      setIsGenerating(false);
      setProcessingStep('');
      avatarGeneratingRef.current = false;
    }
  };





  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="avatar-creation-page">
      {/* Enhanced Background Effects */}
      <div className="aeonx-bg">
        {/* Animated Grid */}
        <div className="aeonx-grid"></div>
        
        {/* Pulse Waves */}
        <div className="aeonx-pulse"></div>
        <div className="aeonx-pulse"></div>
        <div className="aeonx-pulse"></div>
        
        {/* Glowing Orbs */}
        <div className="aeonx-orb aeonx-orb-1"></div>
        <div className="aeonx-orb aeonx-orb-2"></div>
        <div className="aeonx-orb aeonx-orb-3"></div>
        
        {/* Neural Network Nodes */}
        {Array.from({ length: 25 }).map((_, i) => (
          <div 
            key={`node-${i}`}
            className="aeonx-node"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
        
        {/* Animated Particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div 
            key={`particle-${i}`}
            className="aeonx-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 12}s`,
              animationDuration: `${10 + Math.random() * 8}s`
            }}
          />
        ))}
        
        {/* Circuit Lines */}
        {Array.from({ length: 8 }).map((_, i) => (
          <React.Fragment key={`circuit-${i}`}>
            <div 
              className="aeonx-circuit h"
              style={{
                width: `${100 + Math.random() * 200}px`,
                top: `${10 + Math.random() * 80}%`,
                left: `${Math.random() * 50}%`,
                transform: `rotate(${Math.random() * 10 - 5}deg)`
              }}
            />
            <div 
              className="aeonx-circuit v"
              style={{
                height: `${100 + Math.random() * 200}px`,
                left: `${10 + Math.random() * 80}%`,
                top: `${Math.random() * 50}%`,
                transform: `rotate(${Math.random() * 10 - 5}deg)`
              }}
            />
          </React.Fragment>
        ))}
        
        {/* Data Stream Lines */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div 
            key={`stream-${i}`}
            className="aeonx-stream"
            style={{
              width: `${150 + Math.random() * 200}px`,
              top: `${20 + Math.random() * 60}%`,
              left: `${Math.random() * 60}%`,
              transform: `rotate(${Math.random() * 20 - 10}deg)`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="avatar-creation-container" style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
        {/* AeonX Logo */}
        <div className="logo-section">
          <img 
            src="/AeonX Logo.svg" 
            alt="AeonX Logo" 
            className="aeonx-logo"
          />
        </div>

        {/* Event Name */}
        <h1 className="event-name">India AI Impact Summit 2026</h1>

        {/* Instructions */}
        <p className="instructions">
          Create your AI avatar to personalize your experience
        </p>

        {/* Camera/Photo Area */}
        <div className="camera-area">
          {!isCameraOpen && !capturedImage && (
            <div className="camera-placeholder">
              <div className="placeholder-icon">📷</div>
              <p>Camera preview will appear here</p>
            </div>
          )}

          {isCameraOpen && (
            <div className="camera-preview">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}

          {capturedImage && (
            <div className="captured-photo">
              <img 
                src={capturedImage} 
                alt="Captured" 
                className="captured-image"
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        {/* Buttons */}
        <div className="button-section">
          {!isCameraOpen && !capturedImage && (
            <button 
              className="create-avatar-btn"
              onClick={openCamera}
            >
              {'📷 Create Your Avatar'}
            </button>
          )}

          {isCameraOpen && !capturedImage && (
            <div className="camera-buttons">
              <button 
                className="capture-btn"
                onClick={capturePhoto}
              >
                📸 Capture Photo
              </button>
              <button 
                className="cancel-btn"
                onClick={stopCamera}
              >
                Cancel
              </button>
            </div>
          )}

          {capturedImage && !isGenerating && (
            <div className="photo-buttons">
              <button 
                className="retake-btn"
                onClick={retakePhoto}
              >
                🔄 Retake Photo
              </button>
              <button 
                className="generate-btn"
                onClick={generateAvatar}
                disabled={isGenerating}
              >
                ✨ Generate Avatar
              </button>
            </div>
          )}

          {isGenerating && (
            <div className="generating-message">
              <div className="spinner"></div>
              <p>{processingStep || 'Processing...'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AvatarCreation;
